import React from "react";
import { dateFormat } from "../../../utils/helperMethods";

/**
 * 14030 — Stats strip below the publish header on the Questionnaire & Risk
 * Analysis tab. Match the screenshot exactly:
 *
 *   100 questions · ●Complete: 100 · Completed: Apr 9, 2026 ·
 *   ●Auto-scored: 100 · ●SME-scored: 0 | FINDINGS ●100 Compliant
 *
 * Risk-scored segments (Auto/SME/Findings) only render when the caller
 * passes scored counts — keeps the bar quiet for non-entitled viewers.
 */
const Dot = ({ className }) => (
  <span className={`inline-block h-1.5 w-1.5 rounded-full ${className}`} aria-hidden="true" />
);

const Stat = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 text-sm text-gray-700">{children}</span>
);

const Sep = () => <span className="text-gray-300">·</span>;

const VqqStatsBar = ({
  totalQuestions = 0,
  completeCount = 0,
  completedAt,
  autoScoredCount = null,
  smeScoredCount = null,
  compliantCount = null,
  criticalCount = null,
  highCount = null,
  mediumCount = null,
  lowCount = null,
  notScoredCount = null,
  // 14033 AC #11/12 — "Showing X of Y" chip + "Clear filters" affordance when
  // filters are active. Caller passes the visible question count and a
  // callback to reset filters; both default to null/no-op so the stats bar
  // stays compact when filters aren't in use.
  showingCount = null,
  hasActiveFilters = false,
  onClearFilters = null,
}) => {
  const totalLabel = totalQuestions === 1 ? "question" : "questions";
  const showRiskStats = autoScoredCount != null || smeScoredCount != null;
  const showFindings = compliantCount != null;
  const showFilterSummary =
    hasActiveFilters && showingCount != null && showingCount !== totalQuestions;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-sm text-gray-700 bg-gray-50 border-b border-gray-200">
      <Stat>
        <span className="font-semibold text-gray-900 tabular-nums">{totalQuestions}</span>
        <span>{totalLabel}</span>
      </Stat>

      {showFilterSummary ? (
        <>
          <Sep />
          <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 border border-indigo-200 px-2 py-0.5 text-[11px] font-medium text-indigo-700">
            Showing <span className="tabular-nums">{showingCount}</span> of <span className="tabular-nums">{totalQuestions}</span>
          </span>
          {typeof onClearFilters === "function" ? (
            <button
              type="button"
              onClick={onClearFilters}
              className="text-[11px] font-medium text-indigo-700 hover:text-indigo-900 underline-offset-2 hover:underline"
            >
              Clear filters
            </button>
          ) : null}
        </>
      ) : null}

      <Sep />
      <Stat>
        <Dot className="bg-emerald-500" />
        <span className="font-medium text-gray-900">Complete:</span>
        <span className="font-semibold text-gray-900 tabular-nums">{completeCount}</span>
      </Stat>

      {completedAt ? (
        <>
          <Sep />
          <Stat>
            <span className="font-medium text-gray-900">Completed:</span>
            <span className="font-semibold text-gray-900">{dateFormat(completedAt)}</span>
          </Stat>
        </>
      ) : null}

      {showRiskStats ? (
        <>
          <Sep />
          <Stat>
            <Dot className="bg-blue-500" />
            <span className="font-medium text-blue-700">Auto-scored:</span>
            <span className="font-semibold text-blue-700 tabular-nums">{autoScoredCount ?? 0}</span>
          </Stat>
          <Sep />
          <Stat>
            <Dot className="bg-violet-500" />
            <span className="font-medium text-violet-700">SME-scored:</span>
            <span className="font-semibold text-violet-700 tabular-nums">{smeScoredCount ?? 0}</span>
          </Stat>
        </>
      ) : null}

      {showFindings ? (
        <>
          <span className="mx-2 text-gray-300">|</span>
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Findings
          </span>
          {criticalCount ? (
            <Stat>
              <Dot className="bg-red-500" />
              <span className="font-semibold text-red-700 tabular-nums">{criticalCount}</span>
              <span className="text-red-700">Critical</span>
            </Stat>
          ) : null}
          {highCount ? (
            <Stat>
              <Dot className="bg-orange-500" />
              <span className="font-semibold text-orange-700 tabular-nums">{highCount}</span>
              <span className="text-orange-700">High</span>
            </Stat>
          ) : null}
          {mediumCount ? (
            <Stat>
              <Dot className="bg-yellow-500" />
              <span className="font-semibold text-yellow-700 tabular-nums">{mediumCount}</span>
              <span className="text-yellow-700">Medium</span>
            </Stat>
          ) : null}
          {lowCount ? (
            <Stat>
              <Dot className="bg-blue-500" />
              <span className="font-semibold text-blue-700 tabular-nums">{lowCount}</span>
              <span className="text-blue-700">Low</span>
            </Stat>
          ) : null}
          <Stat>
            <Dot className="bg-emerald-500" />
            <span className="font-semibold text-emerald-700 tabular-nums">{compliantCount}</span>
            <span className="text-emerald-700">Compliant</span>
          </Stat>
          {notScoredCount ? (
            <Stat>
              <Dot className="bg-gray-400" />
              <span className="font-semibold text-gray-600 tabular-nums">{notScoredCount}</span>
              <span className="text-gray-600">Not Scored</span>
            </Stat>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default VqqStatsBar;
